/**
 * Every distinct thing a principal can attempt.
 *
 * Capabilities are checked individually and roles are only ever bundles of
 * them — nothing in the system branches on a role name. That indirection is what
 * lets permissions change without touching endpoint code.
 */
export const CAPABILITIES = [
    'tenant:read',
    'tenant:manage',
    'account:read',
    'account:onboard',
    'grant:request',
    'deployment:read',
    'deployment:create',
    'deployment:upgrade',
    'deployment:destroy',
    'deployment:approve',
    'plan:run',
    'outputs:refresh',
    'logs:read',
    'diagnostics:read',
    'diagnostics:bundle',
    'audit:read',
    'version:manage',
    'access:manage',
    /**
     * Hide a customer, an account or an instance from the lists — and restore one.
     *
     * Its own capability rather than reusing `deployment:destroy`, which means
     * tearing down real infrastructure in someone else's account. That a record
     * and the thing it describes can be confused is exactly the mistake worth
     * making unavailable at the type, and one capability covers all three because
     * it is one kind of act rather than three.
     */
    'records:manage',
    /**
     * Decide whether a cloud account is Qrvey's own, and whether it takes new
     * instances (IM-127).
     *
     * Its own capability, and neither of the two it could have been folded into.
     * Not `account:onboard`: that is held by whoever sets an account up and mints
     * its external id, which is a different act from deciding whether the account
     * may take deployments at all. Not `records:manage`: that hides records and
     * deliberately does not touch what may happen to infrastructure.
     *
     * `platform-admin` only. `deployment-engineer` creates instances, and handing
     * the same role the switch that stops them makes the switch decorative.
     */
    'account:settings',
    /**
     * Read what a cloud account is spending (IM-130).
     *
     * Its own capability rather than falling out of `deployment:read`, which is
     * the floor every role builds on and is held by `sales-cs`. An account's AWS
     * bill is commercially sensitive even though it is not customer data in the
     * SEC-4 sense — it says how large a customer's deployment is and what it costs
     * them to run — and IM-75 is the precedent for what happens when a capability
     * is chosen for what a response *contains* rather than for who should be
     * doing the thing.
     *
     * It is also not free: every read charges the customer's account about a cent
     * and lands in their CloudTrail.
     */
    'cost:read',
    /**
     * Set the platform budget over Qrvey's own fleet (IM-152).
     *
     * `platform-admin` only, and not folded into `account:settings`: that is a
     * switch on one account, this is a ceiling that can refuse new instances in
     * every Qrvey-owned account at once. `internal-ops` creates those instances,
     * and handing the same role the ceiling on them would make it decorative —
     * the reasoning `account:settings` already records.
     */
    'budget:manage',
    /**
     * Invite, change and remove the users of one customer (IM-153).
     *
     * Tenant-scoped, and deliberately not `access:manage`: that is the staff
     * screen assigning anyone to any customer. This reaches one customer's own
     * people and only ever grants `customer-admin` or `customer-viewer`. Held by
     * `customer-admin` for their own tenant, and by `platform-admin` so Qrvey can
     * invite a customer's first admin.
     */
    'users:manage',
    /**
     * Choose which commands Qrvey may run in a customer's account without asking
     * them first (IM-102).
     *
     * `customer-admin` only — **deliberately not `platform-admin`**, which
     * otherwise holds everything. The setting is the customer's consent given in
     * advance, and consent chosen by the party it restrains is not consent: staff
     * who could set "auto-approve apply" could approve every apply they start.
     * The handler also refuses any staff actor on the route, so a future role
     * that gained this by accident would still not reach it.
     */
    'approvals:manage',
];
export const isCapability = (value) => CAPABILITIES.includes(value);
//# sourceMappingURL=capabilities.js.map